import { logisticFunction } from "@faro-lotv/foundation";
import { create } from "zustand";

export type PointOpacitySettings = {
  minOpacity: number;
  maxOpacity: number;
  midPoint: number;
  growthRate: number;
};

// Values determined experimentally to give good visual quality for a wide range of projects.
// The goal is to keep walls and other features clearly visible even when zoomed out,
// while still making individual points visible when zoomed in.
const defaultPointOpacitySettings: PointOpacitySettings = Object.freeze({
  minOpacity: 0.008,
  maxOpacity: 0.4,
  midPoint: 3.5,
  // Inverted growth rate as lower points / px should result in higher opacity
  growthRate: -0.55,
});

export type PointOpacitySettingsStore = PointOpacitySettings & {
  setMinOpacity(value: number): void;
  setMaxOpacity(value: number): void;
  setMidPoint(value: number): void;
  setGrowthRate(value: number): void;
  resetToDefault(): void;
};

/** Context containing settings for dynamically setting the point opacity. */
export const usePointOpacitySettingsContext = create<PointOpacitySettingsStore>(
  (set) => ({
    ...defaultPointOpacitySettings,
    setMinOpacity: (value) => set({ minOpacity: value }),
    setMaxOpacity: (value) => set({ maxOpacity: value }),
    setMidPoint: (value) => set({ midPoint: value }),
    setGrowthRate: (value) => set({ growthRate: value }),
    resetToDefault: () => set({ ...defaultPointOpacitySettings }),
  }),
);

/** @returns A function to convert maximum point density to the opacity to use per point. */
export function pointOpacityCurve({
  minOpacity,
  maxOpacity,
  midPoint,
  growthRate,
}: PointOpacitySettings): (pointDensity: number) => number {
  const innerFn = logisticFunction({
    capacity: maxOpacity,
    growthRate,
    midPoint,
  });

  return (pointDensity: number): number =>
    Math.max(minOpacity, innerFn(pointDensity));
}
